import {Injectable} from "@angular/core";
import {HttpResponse} from "@angular/common/http";
import {Observable} from "rxjs";
import {mergeMap} from "rxjs/operators";
import {Md5} from "ts-md5";
import {OrderStatusEnum} from "../store/state/order.state";
import {AccountStatusEnum} from "../store/state/account.state";
import {TokenTypeEnum} from "../token-type.enum";
import {LocaleService} from "../umbrella-common/services/locale.service";
import {EndpointsProvider} from "../umbrella-common/endpoints/endpoint-provider.service";

@Injectable({providedIn: "root"})
export class AuthenticationService {

	private readonly JWT_TOKEN_KEY: string = 'jwt_token';
	private readonly JWT_EXPIRES_AT_KEY: string = 'jwt_expires_at';
	private readonly ACCOUNT_STATUS_KEY: string = 'account_status';
	private readonly USER_FIRST_NAME_KEY: string = 'user_first_name';
	private readonly USER_LAST_NAME_KEY: string = 'user_last_name';
	private readonly ACCOUNT_STATUS: string = 'account_status';
	private readonly ROLES_KEY: string = 'roles';
	private readonly AVAILABLE_ACTIONS_KEY: string = 'available-actions';
	private readonly CURRENT_ROLE_KEY: string = 'current-role';
	private readonly CLIENT_COUNTRY_KEY: string = 'client-country';
	private readonly USER_COUNTRY_KEY: string = 'user-country';
	private readonly EMAIL_KEY: string = 'email';

	constructor(private endpoints: EndpointsProvider, private localeService: LocaleService) {
	}

	login(email: string, password: string): Observable<HttpResponse<any>> {
		const nonce$: Observable<string> = this.endpoints.user.getNonce(email);
		const cnonce = this.generateClientNonce();

		return nonce$.pipe(mergeMap(nonce => this.authenticate(email, password, nonce, cnonce)));
	}

	logout() {
		localStorage.removeItem(this.JWT_TOKEN_KEY);
		localStorage.removeItem(this.JWT_EXPIRES_AT_KEY);
		localStorage.removeItem(this.USER_FIRST_NAME_KEY);
		localStorage.removeItem(this.USER_LAST_NAME_KEY);
		localStorage.removeItem(this.ROLES_KEY);
		localStorage.removeItem(this.CURRENT_ROLE_KEY);
		localStorage.removeItem(this.EMAIL_KEY);
		localStorage.removeItem(this.ACCOUNT_STATUS_KEY);
		localStorage.removeItem(this.AVAILABLE_ACTIONS_KEY);
		localStorage.removeItem(this.CLIENT_COUNTRY_KEY);
		localStorage.removeItem(this.USER_COUNTRY_KEY);

		this.localeService.initLocale();
	}

	isLoggedIn(): boolean {
		const token: string = localStorage.getItem(this.JWT_TOKEN_KEY);
		const expiresAt: number = +localStorage.getItem(this.JWT_EXPIRES_AT_KEY);
		return token && expiresAt && expiresAt > Date.now();
	}

	isDekraEmployeeLogged(): boolean {
		return this.isLoggedUserApplicationReviewer() ||
			this.isLoggedUserClientService() ||
			this.isLoggedAdmin() ||
			this.isLoggedUserCurrentRoleSalesPerson() ||
			this.isLoggedUserPrivacyManager();
	}

	isLoggedUserApplicationReviewer(): boolean {
		return this.isLoggedIn() && isUserApplicationReviewer(this.getUser());
	}

	isLoggedUserClientService(): boolean {
		return this.isLoggedIn() && isUserClientService(this.getUser());
	}

	isLoggedUserCurrentRoleSalesPerson(): boolean {
		return this.isLoggedIn() && isUserSalesPerson(this.getUser());
	}

	isLoggedUserPrivacyManager(): boolean {
		return this.isLoggedIn() && isUserPrivacyManager(this.getUser());
	}

	isLoggedUserWithRole(role: string): boolean {
		return this.isLoggedIn() && isLoggedInWithRole(role);
	}

	isLoggedUserClient(canBeAnonymous ?: boolean): boolean {
		return this.isLoggedIn() && isUserClient(this.getUser()) || canBeAnonymous && this.isLoggedAnonymousClient();
	}

	isLoggedAdmin(): boolean {
		return this.isLoggedIn() && isAdmin(this.getUser());
	}

	isLoggedAnonymousClient() {
		return this.isLoggedIn() && this.getUser().roles.includes('ROLE_ANONYMOUS_CLIENT');
	}

	isAssignedReviewer(email: string): boolean {
		return this.isLoggedUserApplicationReviewer() && this.getUser().email == email;
	}

	isAssignedSalesPerson(email: string): boolean {
		return this.isLoggedUserCurrentRoleSalesPerson() && this.getUser().email == email;
	}

	getToken(): string {
		return localStorage.getItem(this.JWT_TOKEN_KEY);
	}

	getUser(): User | null {
		return {
			email: localStorage.getItem(this.EMAIL_KEY),
			firstName: localStorage.getItem(this.USER_FIRST_NAME_KEY),
			lastName: localStorage.getItem(this.USER_LAST_NAME_KEY),
			fullName: localStorage.getItem(this.USER_FIRST_NAME_KEY) + " " + localStorage.getItem(this.USER_LAST_NAME_KEY),
			roles: localStorage.getItem(this.ROLES_KEY) ? localStorage.getItem(this.ROLES_KEY).split(",") : [],
			accountStatus: localStorage.getItem(this.ACCOUNT_STATUS_KEY) as AccountStatusEnum,
			countryCode: localStorage.getItem(this.USER_COUNTRY_KEY),
			suspended: false,
			availableActions: this.getAvailableActions(),
			permissions: this.getPermissions()
		};
	}

	changeEmail(email: string) {
		localStorage.setItem(this.EMAIL_KEY, email);
	}

	verifyToken(email: string, token: string, tokenType: TokenTypeEnum): Observable<HttpResponse<any>> {
		return this.endpoints.user.verifyToken(email, token, tokenType);
	}

	verifyChangeProposalToken(email: string, token: string): Observable<HttpResponse<any>> {
		return this.endpoints.user.verifyChangeProposalToken(email, token);
	}

	verifyTransferDeclinedToken(email: string, token: string): Observable<HttpResponse<any>> {
		return this.endpoints.user.verifyTransferDeclinedToken(email, token);
	}

	activateAccount(email: string, password: string): Observable<any> {
		return this.endpoints.user.activateAccount(email, password);
	}

	resetPassword(email: string, password: string, token: string): Observable<any> {
		return this.endpoints.user.resetPasswordFromProvidedUrl(email, password, token);
	}

	isAccountActivated() {
		return this.getUser().accountStatus === AccountStatusEnum.ACTIVATED;
	}

	isJwtTokenPresentAndExpired() {
		const token: string = localStorage.getItem(this.JWT_TOKEN_KEY);
		const expiresAt: number = +localStorage.getItem(this.JWT_EXPIRES_AT_KEY);
		return token && expiresAt && expiresAt < Date.now();
	}

	switchCurrentRole(newRole: string): Observable<HttpResponse<any>> {
		return this.endpoints.user.switchCurrentRole(newRole);
	}

	getUserCurrentRole(): string {
		return localStorage.getItem(this.CURRENT_ROLE_KEY);
	}

	setClientCountry(countryCode: string) {
		localStorage.setItem(this.CLIENT_COUNTRY_KEY, countryCode);
	}

	getClientCountry(): string | null {
		return localStorage.getItem(this.CLIENT_COUNTRY_KEY);
	}

	hasPermissions(countryCode: string, actions: UserAction[]): boolean {
		return actions.every(action => this.hasPermission(countryCode, action));
	}

	hasPermission(countryCode: string, action: UserAction): boolean {
		const actions: AvailableActions[] = JSON.parse(localStorage.getItem(this.AVAILABLE_ACTIONS_KEY));

		if (!actions) return false;

		return actions.some(a => {
			if (a.generalActions) {
				return a.availableAction.includes(action);
			}

			return a.countries.includes(countryCode) && a.availableAction.includes(action);
		});
	}

	setCurrentRoleBypassingSecurity(role: string) {
		localStorage.setItem(this.CURRENT_ROLE_KEY, role);
	}

	storeUserData(user: User): void {
		localStorage.setItem(this.USER_FIRST_NAME_KEY, user.firstName);
		localStorage.setItem(this.USER_LAST_NAME_KEY, user.lastName);
		localStorage.setItem(this.EMAIL_KEY, user.email);
		localStorage.setItem(this.ROLES_KEY, user.roles.join(","));
		localStorage.setItem(this.ACCOUNT_STATUS, user.accountStatus);
		localStorage.setItem(this.AVAILABLE_ACTIONS_KEY, JSON.stringify(user.availableActions));
		localStorage.setItem(this.USER_COUNTRY_KEY, user.countryCode);

		if (user.roles.some(role => ['ROLE_CLIENT', 'ROLE_ANONYMOUS_CLIENT'].includes(role))) {
			localStorage.setItem(this.CLIENT_COUNTRY_KEY, user.countryCode);
		}
	}

	setCurrentRole(currentRole: string) {
		localStorage.setItem(this.CURRENT_ROLE_KEY, currentRole);
	}

	storeAuthDetails(jwtToken: string, expiresAt: string, currentRole?: string): void {
		localStorage.setItem(this.JWT_TOKEN_KEY, jwtToken);
		localStorage.setItem(this.JWT_EXPIRES_AT_KEY, expiresAt);
		!!currentRole && localStorage.setItem(this.CURRENT_ROLE_KEY, currentRole);
	}

	private getPermissions(): AvailableActions[] {
		return JSON.parse(localStorage.getItem(this.AVAILABLE_ACTIONS_KEY));
	}

	private getAvailableActions(): UserAction[] {
		const availableActions: AvailableActions[] = JSON.parse(localStorage.getItem(this.AVAILABLE_ACTIONS_KEY));
		return !!availableActions ? availableActions.map(action => action.availableAction) : null;
	}

	private authenticate(username: string, password: string, nonce: string, cnonce: string): Observable<HttpResponse<any>> {
		return this.endpoints.user.authenticate(username, password, nonce, cnonce);
	}

	private generateClientNonce(): string {
		const md5 = new Md5();
		const timeMillis = String(new Date().getTime());
		return md5.appendStr(timeMillis).end() as string;
	}
}

export interface User {
	email: string;
	firstName: string;
	lastName: string;
	fullName: string;
	jobPosition?: string;
	phone?: string;
	mobile?: string;
	countryCode: string;
	roles: string[];
	accountStatus: AccountStatusEnum;
	availableActions: UserAction[];
	permissions: AvailableActions[];
	suspended: boolean;
	signatureBase64?: string;
}

export interface AvailableActions {
	availableAction: UserAction;
	countries: string[],
	generalActions: boolean;
}

export enum UserAction {
	PREVIEW_KPIS = 'PREVIEW_KPIS',
	MANAGE_USERS = 'MANAGE_USERS',
	ACCESS_DASHBOARD = 'ACCESS_DASHBOARD',
	CREATE_USER = 'CREATE_USER',
	EDIT_USER_PERMISSIONS = 'EDIT_USER_PERMISSIONS',
	EDIT_USER_DATA = 'EDIT_USER_DATA',
	DELETE_USER = 'DELETE_USER',
	ACCESS_CONFIG_PANEL = 'ACCESS_CONFIG_PANEL',
	MODIFY_CALCULATION_CONFIG = 'MODIFY_CALCULATION_CONFIG',
	MODIFY_EMAIL_CONFIG = 'MODIFY_EMAIL_CONFIG',
	REASSIGN_SALES_PERSON = 'REASSIGN_SALES_PERSON',
	REASSIGN_APPLICATION_REVIEWER = 'REASSIGN_APPLICATION_REVIEWER',
	ARCHIVE_SALES_LEAD = 'ARCHIVE_SALES_LEAD',
	PREVIEW_TRANSLATIONS = 'PREVIEW_TRANSLATIONS',
	FILL_TRANSLATIONS = 'FILL_TRANSLATIONS',
	EDIT_TRANSLATIONS = 'EDIT_TRANSLATIONS',
	PREVIEW_AR_CHECKLIST = 'PREVIEW_AR_CHECKLIST',
	EDIT_AR_CHECKLIST = 'EDIT_AR_CHECKLIST',
	PERFORM_APPLICATION_REVIEW = 'PERFORM_APPLICATION_REVIEW',
	PREVIEW_INTERNAL_DOCUMENTS = 'PREVIEW_INTERNAL_DOCUMENTS',
	PREVIEW_APPLICATION_REVIEW = 'PREVIEW_APPLICATION_REVIEW',
	PREVIEW_SALES_LEAD = 'PREVIEW_SALES_LEAD',
	MODIFY_INTERNAL_DOCUMENTS = 'MODIFY_INTERNAL_DOCUMENTS',
	MODIFY_CHANGE_PROPOSAL = 'MODIFY_CHANGE_PROPOSAL',
	SEND_CHANGE_PROPOSAL = 'SEND_CHANGE_PROPOSAL',
	CONTACT_PREVIOUS_CB = 'CONTACT_PREVIOUS_CB',
	SEND_QUOTE_TO_SALES_FORCE = 'SEND_QUOTE_TO_SALES_FORCE',
	TAKEOVER_SALES_LEAD = 'TAKEOVER_SALES_LEAD',
	MODIFY_TAKEOVER_SETTINGS = 'MODIFY_TAKEOVER_SETTINGS',
	ISSUE_MAINTENANCE_INFO = 'ISSUE_MAINTENANCE_INFO',
	CHANGE_SALES_PEOPLE_CLIENT_LIST = 'CHANGE_SALES_PEOPLE_CLIENT_LIST'
}

export function isUserApplicationReviewer(user: User): boolean {
	return localStorage.getItem('current-role') === 'ROLE_APPLICATION_REVIEWER';
}

export function isUserTechnicalReviewer(user: User): boolean {
	return localStorage.getItem('current-role') === 'ROLE_TECHNICAL_REVIEWER';
}

export function isUserClientService(user: User): boolean {
	return localStorage.getItem('current-role') === 'ROLE_CLIENT_SERVICE';
}

export function isUserCertOfficial(user: User): boolean {
	return localStorage.getItem('current-role') === 'ROLE_CERT_OFFICIAL';
}

export function isUserSalesPerson(user: User): boolean {
	return localStorage.getItem('current-role') === 'ROLE_SALES_PERSON';
}

export function isUserPrivacyManager(user: User): boolean {
	return localStorage.getItem('current-role') === 'ROLE_PRIVACY_MANAGER';
}

export function isUserClient(user: User): boolean {
	return localStorage.getItem('current-role') === 'ROLE_CLIENT';
}

export function isAdmin(user: User): boolean {
	return localStorage.getItem('current-role') === 'ROLE_ADMIN';
}

export function isLoggedInWithRole(role: string) {
	return localStorage.getItem('current-role') === role;
}

export interface JwtTokenDetails {
	token: string;
	tokeExpiresAt: Date;
}

export interface ValidResourcesResponse {
	validResources: ValidResource[];
}

export interface ValidResource {
	correlationId: string;
	status: OrderStatusEnum;
	type: ResourceType;
}

export enum ResourceType {
	ORDER = 'ORDER',
	AUDIT = 'AUDIT'
}
